import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Grid, Stepper, Step, StepButton, styled } from '@mui/material';

import './APIResReqStyle.css';

import useAPIResReqStore, { getAPIResReqStore } from './useAPIResReqStore';
import { getCurrentTime } from '../../utils/getCurrentTime';
import { send_component_save_request, send_request } from '../../utils/Request';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';
import { getAPIResReqSaveStore } from './useAPIResReqSaveStore';
import SetUpRequest from './Components/SetUpRequest';
import SetUpPlaceholders from './Components/SetUpPlaceholders';
import Finish from './Components/Finish';
import SetUpSample from './Components/SetUpSample';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';
import CurlTemplatesSetup from './Components/CurlTemplates/CurlTemplatesSetup';
import CustomPaper from '../common/CustomPaper';

import { addCompleter } from 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-noconflict/ace';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/theme-monokai';

const CurlSetupPaper = styled(CustomPaper)({
  paddingBottom: '16px',
  marginBottom: '16px',
});

const APIResReqTemplate = (props) => {
  const [loading, setLoading] = useState(false);
  const [placeholders, setPlaceholders] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [apiStep, setApiStep] = useState(0);
  const [error, setError] = useState(false);
  const errorRef = useRef(false);
  errorRef.current = error;
  const [focus, setFocus] = useState(null);
  const [requestKeyPlaceholders, setRequestKeyPlaceholders] = useState({});
  const [requestValuePlaceholders, setRequestValuePlaceholders] = useState({});
  const [initialLoad, setInitialLoad] = useState(true);

  const store = useMemo(() => getAPIResReqStore(props.component_id), [props.component_id]);

  const componentName = useProjectStore((s) => s.componentName);
  const componentDescription = useProjectStore((s) => s.componentDescription);
  const selectedPlaceholder = useProjectStore((s) => s.selectedPlaceholder);

  useEffect(() => {
    if (props.component_id === undefined) return;

    // If store was prefilled from canvas load, skip the REST fetch
    if (store.getState().loaded) {
      props.onComponentNameChanged(store.getState().name);
      props.onLastModifiedChanged(store.getState().lastModified);

      const responseData = store.getState().responseData;
      if (responseData?.samplePlaceholders) {
        setPlaceholders(responseData.samplePlaceholders);
        if (responseData.json !== '' && responseData.samplePlaceholders.length > 0) {
          setActiveStep(3);
          setApiStep(3);
        }
      }
      setLoading(false);
      props.setChildTemplateLoaded(true);
      return;
    }

    setLoading(true);

    send_request(
      `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
      '',
      {}
    )
      .then((response) => {
        if (response && response.data) {
          const { components } = response.data;
          if (components) {
            const storeState = store.getState();
            storeState.setRequestTemplate(components[0]);
            storeState.setSampleTemplate(components[0]);
            storeState.setResponseTemplate(components[0]);
            storeState.setCurlTemplateData(components[0]);
            store.setState({ loaded: true });
            props.onComponentNameChanged(store.getState().name);
            props.onComponentDescriptionChanged(components[0].description);
            props.onLastModifiedChanged(store.getState().lastModified);

            if (components[0].componentData) {
              if (components[0].componentData.responseData) {
                if (
                  !components[0].componentData.responseData.samplePlaceholders &&
                  components[0].componentData.responseData.placeholders
                ) {
                  const newResponseData = {
                    ...store.getState().responseData,
                    samplePlaceholders: [],
                  };
                  for (let i = 0; i < components[0].componentData.responseData.placeholders.length; i++) {
                    components[0].componentData.responseData.placeholders[i].checked = true;
                    newResponseData.samplePlaceholders.push(
                      components[0].componentData.responseData.placeholders[i]
                    );
                  }
                  store.setState({ responseData: newResponseData });
                }
                setPlaceholders(components[0].componentData.responseData.samplePlaceholders || []);
                if (
                  components[0].componentData.responseData.json !== '' &&
                  components[0].componentData.responseData.samplePlaceholders != []
                ) {
                  setActiveStep(3);
                  setApiStep(3);
                }
                setLoading(false);
                props.setChildTemplateLoaded(true);
              }
            } else {
              const lp = [];
              lp.push({
                index: 0,
                placeholders: [],
                key: store.getState().keyAuto(),
                incomingKey: '',
              });
              setPlaceholders(lp);
              setLoading(false);
              props.setChildTemplateLoaded(true);
            }
          }
        }

        props.setChildTemplateLoaded(true);
        setLoading(false);
      })
      .catch((err) => {
        throw Error(err.message);
      });

    const placeholderParameters = [];
    if (
      props.availablePlaceholders &&
      props.availablePlaceholders.length === 1 &&
      props.availablePlaceholders[0] &&
      props.availablePlaceholders[0].placeholders &&
      props.availablePlaceholders[0].placeholders.length > 0
    ) {
      for (let i = 0; i < props.availablePlaceholders[0].placeholders.length; i++) {
        const completeKey = '${' + props.availablePlaceholders[0].placeholders[i].key + '}';
        const newCompleteItem = {
          name: completeKey,
          value: completeKey,
          caption: completeKey,
          meta: 'local',
          score: 1000,
        };
        placeholderParameters.push(newCompleteItem);
      }
    }
    addCompleter({
      getCompletions: function (editor, session, pos, prefix, callback) {
        callback(null, placeholderParameters);
      },
    });
  }, []);

  useEffect(() => {
    store.getState().setRequestName(componentName);
  }, [componentName]);

  useEffect(() => {
    if (!props.component_id) return;
    const saveStore = getAPIResReqSaveStore(props.component_id);
    saveStore.getState().registerSave(async () => {
      const currentState = store.getState();
      const { placeholderError } = currentState;
      if (errorRef.current || (placeholderError && placeholderError.length > 0)) {
        props.showError();
        return;
      }

      const dataComponent = JSON.parse(JSON.stringify(currentState.responseData));
      const newResponseData = { ...currentState.responseData, placeholders: [] };
      if (dataComponent.samplePlaceholders) {
        for (let i = 0; i < dataComponent.samplePlaceholders.length; i++) {
          if (dataComponent.samplePlaceholders[i].checked == true) {
            newResponseData.placeholders.push(dataComponent.samplePlaceholders[i]);
          }
        }
      }
      store.setState({ responseData: newResponseData });

      const lastModified = getCurrentTime();
      currentState.setLastModified(lastModified);

      const updatedStore = store.getState();
      const requestTemplate = updatedStore.getRequestTemplate();
      const sampleTemplate = updatedStore.getSampleTemplate();
      const responseTemplate = updatedStore.getResponseTemplate();
      const curlTemplateData = JSON.parse(JSON.stringify(updatedStore.curlTemplateData));

      const data = injectMetadataFields({
        componentData: {
          name: store.getState().name,
          requestData: requestTemplate.requestData,
          sampleData: sampleTemplate.sampleData,
          responseData: responseTemplate.responseData,
          lastModified: lastModified,
          data: {
            curlTemplateData: curlTemplateData,
          },
        },
        componentId: requestTemplate.componentId ? requestTemplate.componentId : props.component_id,
        description: store.getState().componentDescription,
      });

      const response = await send_component_save_request(
        'component-service/apirequestandresponse/data',
        data,
        '',
        'POST'
      );
      if (response && response.status === 200) {
        props.showSuccess(data);
        props.onLastModifiedChanged(lastModified);
        useProjectStore.setState({ savedComponent: true });
      }
      return response;
    });

  }, [props.component_id]);

  useEffect(() => {
    const unsub = useProjectStore.subscribe(
      (s) => s.selectedPlaceholder,
      (placeholder) => {
        const activeId = useProjectStore.getState().activeComponentId;
        if (activeId !== null && activeId !== props.component_id) return;
        if (!placeholder || !placeholder['selected'] || typeof placeholder['selected'] !== 'object') return;
        const { style, key } = placeholder['selected'];
        if (!style) return;
        insertPlaceholder(style.default, key, style, focus);
      }
    );
    return unsub;
  }, [focus]);

  const insertPlaceholder = useCallback(
    (label, text, style, currentFocus) => {
      if (typeof currentFocus === 'object' && currentFocus !== null) {
        const { headerType, index, step } = currentFocus;
        const placeholder = '${' + text + '}';
        if (headerType === 'key') {
          if (step === 'request') {
            const reqState = store.getState();
            const headers = [...reqState.requestData.headers];
            headers[index] = { ...headers[index], header_name: headers[index].header_name + placeholder };
            store.setState({ requestData: { ...reqState.requestData, headers } });

            const newKeysPlaceholders = { ...requestKeyPlaceholders };
            if (!(index in newKeysPlaceholders)) {
              newKeysPlaceholders[index] = [{ text }];
            } else {
              newKeysPlaceholders[index] = [...newKeysPlaceholders[index], { text }];
            }
            setRequestKeyPlaceholders(newKeysPlaceholders);
          }
        } else {
          if (step === 'request') {
            const reqState = store.getState();
            const headers = [...reqState.requestData.headers];
            headers[index] = { ...headers[index], header_value: headers[index].header_value + placeholder };
            store.setState({ requestData: { ...reqState.requestData, headers } });

            const newValuePlaceholders = { ...requestValuePlaceholders };
            if (!(index in newValuePlaceholders)) {
              newValuePlaceholders[index] = [{ text }];
            } else {
              newValuePlaceholders[index] = [...newValuePlaceholders[index], { text }];
            }
            setRequestValuePlaceholders(newValuePlaceholders);
          }
        }
        return;
      }

      if (currentFocus === 'editor') {
        setPlaceholders((prev) => [...prev, { label, text, style, focus: currentFocus }]);
      } else if (currentFocus === 'body') {
        const formattedText = '${' + text + '}';
        const template = store.getState().getTemplate();
        const newText = template.data.body + formattedText;
        store.getState().setApiReqBody(newText);
      } else if (currentFocus === 'endpoint') {
        const store = store.getState();
        const eps = [...store.endpointPlaceholders, { label, text, style }];
        store.setEndpointPlaceholders(eps);
      }
    },
    [requestKeyPlaceholders, requestValuePlaceholders]
  );

  const onChangeState = (newApiStep, newActiveStep) => {
    setApiStep(newApiStep);
    setActiveStep(newActiveStep);
  };

  const handleOnClickStep = (step) => {
    setApiStep(step);
    setActiveStep(step);
  };

  const getStepContent = (step, isExpanded) => {
    switch (step) {
      case 0:
        return (
          <SetUpRequest
            placeholders={placeholders}
            keyPlaceholders={requestKeyPlaceholders}
            valuePlaceholders={requestValuePlaceholders}
            availablePlaceholders={props.availablePlaceholders}
            onChangeState={(s, a) => onChangeState(s, a)}
            initialLoad={initialLoad}
            setInitialLoad={setInitialLoad}
            onFocus={(f) => setFocus(f)}
            setOnFocus={(state) => setFocus(state)}
            isExpanded={isExpanded}
          />
        );
      case 1:
        return (
          <SetUpSample
            availablePlaceholders={props.availablePlaceholders}
            onChangeState={(s, a) => onChangeState(s, a)}
            componentStore={store}
            project_id={props.project_id}
            component_id={props.component_id}
          />
        );
      case 2:
        return (
          <SetUpPlaceholders
            availablePlaceholders={props.availablePlaceholders}
            componentStore={store}
            onChangeState={(s, a) => onChangeState(s, a)}
            handleKeyChanges={(err) => setError(err)}
            stepStatus={'setPlaceholders'}
          />
        );
      case 3:
        return (
          <Finish
            availablePlaceholders={props.availablePlaceholders}
            onChangeState={(s, a) => onChangeState(s, a)}
            stepStatus={'finish'}
          />
        );
      default:
        return 'Unknown step';
    }
  };

  const requestData = store((s) => s.requestData);
  const steps = ['Set up request', 'Set up sample', 'Set up placeholders', 'Finish'];
  const isCurlSetupMode = requestData.setupMode !== 'CURL';

  if (!loading) {
    const shouldHide = !requestData?.skipSample;
    return (
      <Grid container>
        <Grid item xs={12}>
          <CustomPaper className="componentPaper" hidden={requestData && requestData.skipSample}>
            <Stepper activeStep={activeStep}>
              {steps.map((label, index) => {
                const stepProps = {};
                const labelProps = {};
                return (
                  <Step key={label} {...stepProps}>
                    <StepButton onClick={() => handleOnClickStep(index)} {...labelProps}>
                      {label}
                    </StepButton>
                  </Step>
                );
              })}
            </Stepper>
            {getStepContent(activeStep, props.isExpanded)}
          </CustomPaper>
        </Grid>
        <Grid item xs={12}>
          <CurlSetupPaper className="componentPaper" hidden={isCurlSetupMode}>
            <CurlTemplatesSetup
              availablePlaceholders={props.availablePlaceholders}
              setOnFocus={(state) => setFocus(state)}
              componentStore={store}
            />
          </CurlSetupPaper>
        </Grid>
        <Grid item xs={12}>
          <CustomPaper className="componentPaper" hidden={shouldHide}>
            <SetUpRequest
              placeholders={placeholders}
              keyPlaceholders={requestKeyPlaceholders}
              valuePlaceholders={requestValuePlaceholders}
              availablePlaceholders={props.availablePlaceholders}
              onFocus={(f) => setFocus(f)}
              setOnFocus={(state) => setFocus(state)}
              isExpanded={props.isExpanded}
            />
          </CustomPaper>
        </Grid>
      </Grid>
    );
  } else {
    return <IndividualComponentLoader />;
  }
};

export default APIResReqTemplate;
